import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, Role } from './AuthContext';

// Roles that can impersonate lower tiers (admin > creator/customer/field_agent).
// field_agent is ranked alongside creator/customer — it's a peer, not a hierarchy step.
const RANK: Record<Role, number> = {
  public: 0, precustomer: 0, customer: 1, creator: 1, field_agent: 1, admin: 3,
};

/**
 * Guard a route by role.
 *   role=<Role>     — rank-based (admin can impersonate customer, etc.)
 *   role=<Role[]>   — explicit allowlist (exact match). Use this for
 *                     peer roles like field_agent where rank doesn't imply access.
 */
export function RoleGuard({ role, children }: { role: Role | Role[]; children: ReactNode }) {
  const { user, role: current, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  // Race guard: if the session is authed but `role` is still 'public', the
  // user_roles lookup is either still in-flight, or resolved to no row, OR
  // silently failed. Booting to '/' in that window makes refresh feel broken
  // ("clicking Dashboard does nothing"). Block the render instead — once the
  // role settles to its real value we'll re-render into the protected tree.
  if (user && current === 'public') return null;
  if (Array.isArray(role)) {
    if (!role.includes(current)) return <Navigate to="/" replace />;
  } else {
    if (RANK[current] < RANK[role]) return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
